import React, { useEffect, useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Link, 
  Container,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { RoutesEnum } from '../../utils/routes';
import { StatusAccountEnum } from '../../utils/StatusAccountEnum';
import { ga4 } from '../../utils/analytics/ga4';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [planSelectionUrl, setPlanSelectionUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const redirectParam = searchParams.get('redirect');
  const redirectTarget = redirectParam && redirectParam.startsWith('/') ? redirectParam : null;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTarget || '/imigra-pulse-canada/painel');
    }
  }, [isAuthenticated, navigate, redirectTarget]);

  useEffect(() => {
    ga4.event('login_viewed');
  }, []);

  useEffect(() => {
    const isPt = i18n.language?.toLowerCase().startsWith('pt');
    document.documentElement.lang = isPt ? 'pt-BR' : 'en';

    const title = 'Login | Imigra Pulse Canada';
    const description = isPt
      ? 'Acesse sua conta para gerenciar notificacoes e configuracoes do Imigra Pulse Canada.'
      : 'Sign in to manage notifications and settings in Imigra Pulse Canada.';
    const canonical = `${window.location.origin}/login`;
    const head = document.head;

    const upsertMeta = (selector: string, attrs: Record<string, string>, content: string) => {
      let el = head.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        for (const [k, v] of Object.entries(attrs)) {
          el.setAttribute(k, v);
        }
        head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const upsertLink = (selector: string, attrs: Record<string, string>, href: string) => {
      let el = head.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        for (const [k, v] of Object.entries(attrs)) {
          el.setAttribute(k, v);
        }
        head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, 'noindex,nofollow');
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot' }, 'noindex,nofollow');
    upsertLink('link[rel="canonical"]', { rel: 'canonical' }, canonical);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonical);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
  }, [i18n.language]);

  // Clear possible browser-remembered values on mount to avoid pre-population
  useEffect(() => {
    setEmail('');
    setPassword('');
    const timer = setTimeout(() => {
      setEmail('');
      setPassword('');
    }, 120);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
     
    setError('');   
    setPlanSelectionUrl(null);
    setLoading(true);
   
    // Simple validation
    if (!email || !password) {
      setError(t('login_error_fill_fields'));
      setLoading(false);
      return;
    }

    try {
      const res = await login(email, password);
      if (res?.success && res?.data) {
        const { statusAccount, userAccessGuid, shouldRedirectToResetPassword, shouldRedirectToConfirmToken } = res.data;

        // New schema: if shouldRedirectToResetPassword is true, redirect user to reset page
        if (shouldRedirectToResetPassword === true) {
          navigate('/reset-password');
          return;
        }

        if (shouldRedirectToConfirmToken === true) {
          navigate(`/confirmacao-token?guid=${encodeURIComponent(userAccessGuid)}`);
          return;
        }
        // Redirect logic based on statusAccount
        if (statusAccount === StatusAccountEnum.PENDING_TO_CONFIRM_EMAIL) {
           // Pending email confirmation: do NOT authenticate here
           navigate(`/confirmacao-token?guid=${encodeURIComponent(userAccessGuid)}`);

        } else if (statusAccount === StatusAccountEnum.INACTIVE) {
           // Inactive account: do not authenticate, show alert
           ga4.event('login_failed', { reason: 'inactive_user' });
           setError(t('login_error_inactive_user'));
        } else if (statusAccount === StatusAccountEnum.ACTIVE) {
           // Active account: authenticated by context, go to home/dashboard
           ga4.event('login_success');
           navigate(redirectTarget || (RoutesEnum.DASHBOARD.HREF(t) as string));
         } else {
           // NONE or unknown status
           ga4.event('login_failed', { reason: 'invalid_status' });
           setError(t('login_error_invalid_status'));
         }
      } else {
        ga4.event('login_failed', { reason: 'invalid_credentials' });
        setError(res?.message || t('login_error_invalid_credentials'));
      }
    } catch (err: any) {
      const respData = err?.response?.data;
      const messages: string[] = [];
      let planSelectionGuid: string | null = null;
      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) messages.push(msg);
            if (item?.propertyName === 'planSelection' && typeof item?.errorCode === 'string' && item.errorCode) {
              planSelectionGuid = item.errorCode;
            }
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          messages.push(respData.message);
        }
      }
      ga4.event('login_failed', { reason: 'api_exception' });
      setError(messages.length ? messages.join(' ') : t('login_error_generic'));
      if (planSelectionGuid) {
        try {
          const normalizedEmail = String(email || '').trim();
          if (normalizedEmail) {
            sessionStorage.setItem('wg_plan_selection_email', normalizedEmail);
          }
        } catch {}
        const planSelectionPath = RoutesEnum.PLAN_SELECTION.HREF(t) as string;
        setPlanSelectionUrl(`${planSelectionPath}?guid=${encodeURIComponent(planSelectionGuid)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 3 }}>
            WG Tech IT
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {planSelectionUrl ? (
                <>
                  {error.includes('clique aqui') ? (
                    <>
                      {error.split('clique aqui')[0]}
                      <Link
                        href={planSelectionUrl}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(planSelectionUrl);
                        }}
                        underline="always"
                        sx={{ cursor: 'pointer' }}
                      >
                        clique aqui
                      </Link>
                      {error.split('clique aqui').slice(1).join('clique aqui')}
                    </>
                  ) : (
                    <>
                      {error}{' '}
                      Para normalizar a sua conta, assine um dos planos,{' '}
                      <Link
                        href={planSelectionUrl}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(planSelectionUrl);
                        }}
                        underline="always"
                        sx={{ cursor: 'pointer' }}
                      >
                        clicando aqui
                      </Link>
                      .
                    </>
                  )}
                </>
              ) : (
                error
              )}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }} autoComplete="off">
            {/* Autofill decoys to prevent browser from pre-populating real fields */}
            <input type="text" name="email" autoComplete="username" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
            <input type="password" name="password" autoComplete="current-password" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
            <TextField
              margin="normal"
              required
              fullWidth
              id="wg-login-email-id"
              label={t('login_label_email')}
              name="wg-login-email"
              autoComplete="off"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputProps={{ maxLength: 80, autoComplete: 'off' }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="wg-login-password"
              label={t('login_label_password')}
              type={showPassword ? 'text' : 'password'}
              id="wg-login-password-id"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputProps={{ maxLength: 15, autoComplete: 'new-password' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? t('login_aria_hide_password') : t('login_aria_show_password')}
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, bgcolor: 'primary.main' }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('login_btn_submit')}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link href={t(RoutesEnum.RESET_PASSWORD.HREF(t))} variant="body2">
                {t('login_link_forgot_password')}
              </Link>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate(t(RoutesEnum.CREATE_ACCOUNT.HREF(t)) as string)}
                sx={{ textTransform: 'none', width: '100%' }}
              >
                {t('login_btn_create_account')}
              </Button>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                color="primary"
                onClick={() => navigate('/')}
                sx={{ textTransform: 'none' }}
              >
                {t('login_btn_back_home')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
